import type { TFunction } from 'i18next';
import type {
  MyTasks,
  RepCompletedRound,
  RepTaskDeviation,
  RoundToDo,
  TaskRound,
} from '../../api/safetyRounds';
import type {
  CompletedTask,
  SafetyRepTask,
  SafetyRepWork,
} from '../../api/safetyRepWork';
import { deviationPath } from '../SafetyRounds/Deviations/deviationView';
import { shortDate } from '../SafetyRounds/Plans/plansView';
import { protocolPath } from '../SafetyRounds/Protocol/protocolView';
import {
  completedRoute,
  completedTitle,
  completedWhen,
  taskKey,
} from './safetyRepWorkView';

/*
 * «Mine oppgaver» for the safety representative: the safety rounds and the HMS work in one list.
 * The API works out what waits for them. What is here merges it, sorts it and writes it out.
 */

export type Category = 'rounds' | 'deviations' | 'documents';
export type Filter = 'all' | Category;
export const FILTERS: Filter[] = ['all', 'rounds', 'deviations', 'documents'];

export type RepRoundTasks = NonNullable<MyTasks['safetyRep']>;

/** One thing to do, whatever it comes from */
export type ToDo =
  | {
      kind: 'round';
      key: string;
      category: 'rounds';
      deadline: string;
      round: RoundToDo;
    }
  | {
      kind: 'protocol';
      key: string;
      category: 'rounds';
      deadline: null;
      since: string;
      round: TaskRound;
    }
  | {
      kind: 'deviation';
      key: string;
      category: 'deviations';
      deadline: null;
      since: string;
      deviation: RepTaskDeviation;
    }
  | {
      kind: 'hms';
      key: string;
      category: 'documents';
      deadline: string | null;
      since: string;
      task: SafetyRepTask;
    };

const day = (value: string) => value.slice(0, 10);

/**
 * Everything waiting for the safety representative. What only waits on them and has no deadline comes
 * first, the oldest first; then the rest by deadline. The HMS work (risk, plans, documentation and
 * versions of the setup) is all filed under «Dokumenter».
 */
export const toDoList = (
  rounds: RepRoundTasks | null,
  work: SafetyRepWork | null,
): ToDo[] => {
  const items: ToDo[] = [
    ...(rounds?.roundsToDo ?? []).map(
      (round): ToDo => ({
        kind: 'round',
        key: `round-${round.planId}`,
        category: 'rounds',
        deadline: round.nextDate,
        round,
      }),
    ),
    // A protocol waiting for the general manager is not the safety representative's to act on
    ...(rounds?.protocols ?? [])
      .filter((round) => round.status === 'IN_PROGRESS')
      .map(
        (round): ToDo => ({
          kind: 'protocol',
          key: `protocol-${round.id}`,
          category: 'rounds',
          deadline: null,
          since: round.changeRequest?.at ?? round.completedOn ?? '',
          round,
        }),
      ),
    ...(rounds?.toCheck ?? []).map(
      (deviation): ToDo => ({
        kind: 'deviation',
        key: `deviation-${deviation.id}`,
        category: 'deviations',
        deadline: null,
        since: deviation.lastEvent?.at ?? '',
        deviation,
      }),
    ),
    ...(work?.tasks ?? []).map(
      (task): ToDo => ({
        kind: 'hms',
        key: `hms-${taskKey(task)}`,
        category: 'documents',
        deadline: task.due ? day(task.due) : null,
        since: task.since,
        task,
      }),
    ),
  ];
  const since = (item: ToDo) => ('since' in item ? item.since : '');
  return items.sort((a, b) => {
    if (a.deadline === null || b.deadline === null)
      return a.deadline === b.deadline
        ? since(a).localeCompare(since(b))
        : a.deadline === null
          ? -1
          : 1;
    return a.deadline.localeCompare(b.deadline) || a.key.localeCompare(b.key);
  });
};

export const countsOf = (items: ToDo[]): Record<Filter, number> => ({
  all: items.length,
  rounds: items.filter((item) => item.category === 'rounds').length,
  deviations: items.filter((item) => item.category === 'deviations').length,
  documents: items.filter((item) => item.category === 'documents').length,
});

export const filtered = (items: ToDo[], filter: Filter) =>
  filter === 'all' ? items : items.filter((item) => item.category === filter);

/** The menu shows the same count as «Å gjøre» */
export const toDoCount = (
  roundsCount: number | null | undefined,
  work: { tasks: unknown[] } | null | undefined,
) => (roundsCount ?? 0) + (work?.tasks.length ?? 0);

const daysBetween = (from: string, to: string) =>
  Math.round(
    (Date.parse(`${day(to)}T00:00:00Z`) -
      Date.parse(`${day(from)}T00:00:00Z`)) /
      86_400_000,
  );

export type DeadlineState = 'overdue' | 'close' | 'later';

/** Red past the deadline, marked with three days or less left */
export const deadlineState = (
  deadline: string,
  today: string,
): DeadlineState => {
  const left = daysBetween(today, deadline);
  return left < 0 ? 'overdue' : left <= 3 ? 'close' : 'later';
};

const K = 'safetyRep.myTasks';

/** «Forfalt 30. sep.», «Frist i dag», «Frist om 3 dager» or «Frist 8. okt.» */
export const deadlineText = (
  deadline: string,
  today: string,
  t: TFunction,
  language: string,
) => {
  const left = daysBetween(today, deadline);
  if (left < 0)
    return t(`${K}.overdue`, { date: shortDate(deadline, language, false) });
  if (left === 0) return t(`${K}.dueToday`);
  if (left === 1) return t(`${K}.dueTomorrow`);
  if (left <= 3) return t(`${K}.dueIn`, { count: left });
  return t(`${K}.due`, { date: shortDate(deadline, language, false) });
};

/** The most urgent get the filled button: no deadline, overdue, or a week or less left */
export const isUrgent = (item: ToDo, today: string) =>
  item.deadline === null || daysBetween(today, item.deadline) <= 7;

/** «i dag», «i går» or «2. okt.» */
export const relativeDay = (
  value: string,
  today: string,
  t: TFunction,
  language: string,
) => {
  const ago = daysBetween(value, today);
  if (ago === 0) return t(`${K}.today`);
  if (ago === 1) return t(`${K}.yesterday`);
  return shortDate(value, language, false);
};

/** «Godkjenn: Brannslukker ved port 2», so every button has its own name */
export const named = (action: string, title: string) => `${action}: ${title}`;

export const deviationTitle = (deviation: { number: string; title: string }) =>
  `${deviation.number} ${deviation.title}`;

/** Who marked the deviation as fixed, and when */
export const fixedText = (
  deviation: RepTaskDeviation,
  today: string,
  t: TFunction,
  language: string,
) => {
  const event = deviation.lastEvent;
  if (!event || event.type !== 'FIXED') return t(`${K}.fixedUnknown`);
  const when = relativeDay(event.at, today, t, language);
  return event.actorEmail
    ? t(`${K}.fixedBy`, { who: event.actorEmail, when })
    : t(`${K}.fixed`, { when });
};

/** The deviations the safety representative follows, by deadline */
export const followingList = (rounds: RepRoundTasks | null) =>
  [...(rounds?.following ?? [])].sort(
    (a, b) => a.dueDate.localeCompare(b.dueDate) || a.id - b.id,
  );

/** Sent back to the responsible after the safety representative found it was not fixed */
export const wasSentBack = (deviation: RepTaskDeviation) =>
  deviation.lastEvent?.type === 'SENT_BACK';

export type Done = {
  key: string;
  title: string;
  when: string;
  at: string;
  link: string;
};

export const COMPLETED_LIMIT = 50;

const roundDone = (
  entry: RepCompletedRound,
  t: TFunction,
  language: string,
): Done =>
  entry.kind === 'protocol'
    ? {
        key: `protocol-${entry.id}`,
        title: t(`${K}.completed.protocol`, { plan: entry.planName }),
        when: t(
          entry.status === 'SIGNED'
            ? `${K}.completed.signed`
            : `${K}.completed.waiting`,
          {
            date: shortDate(entry.at, language),
          },
        ),
        at: entry.at,
        link: protocolPath(entry.id),
      }
    : {
        key: `deviation-${entry.id}`,
        title: deviationTitle(entry),
        when: t(`${K}.completed.closed`, {
          date: shortDate(entry.at, language),
        }),
        at: entry.at,
        link: deviationPath(entry.id),
      };

const hmsDone = (
  task: CompletedTask,
  t: TFunction,
  language: string,
): Done => ({
  key: `hms-${task.key}`,
  title: completedTitle(task, t),
  when: completedWhen(task, t, language),
  at: task.at,
  link: completedRoute(task),
});

/** What the safety representative has done, newest first, and no more than fifty */
export const completedList = (
  rounds: RepRoundTasks | null,
  work: SafetyRepWork | null,
  t: TFunction,
  language: string,
): Done[] =>
  [
    ...(rounds?.completed ?? []).map((entry) => roundDone(entry, t, language)),
    ...(work?.completed ?? []).map((task) => hmsDone(task, t, language)),
  ]
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
    .slice(0, COMPLETED_LIMIT);
